/**
 * Extension to MIME type mapping for supported file types
 */
const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  // Text files
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",

  // Programming languages
  js: "text/javascript",
  mjs: "text/javascript",
  jsx: "text/javascript",
  ts: "application/typescript",
  tsx: "application/typescript",
  py: "text/x-python",
  java: "text/x-java",
  c: "text/x-c",
  cpp: "text/x-c++",
  cc: "text/x-c++",
  cxx: "text/x-c++",
  h: "text/x-c",
  hpp: "text/x-c++",
  cs: "text/x-csharp",
  php: "application/x-httpd-php",
  rb: "application/x-ruby",
  go: "text/x-go",
  rs: "text/x-rust",
  swift: "text/x-swift",
  kt: "text/x-kotlin",
  scala: "text/x-scala",
  pl: "application/x-perl",
  sh: "application/x-sh",
  bash: "application/x-bash",
  zsh: "application/x-zsh",
  fish: "application/x-fish",
  csh: "application/x-csh",
  tcl: "application/x-tcl",

  // Web technologies
  html: "text/html",
  htm: "text/html",
  css: "text/css",
  scss: "text/x-scss",
  sass: "text/x-sass",
  less: "text/x-less",

  // Data formats
  json: "application/json",
  xml: "application/xml",
  yaml: "application/x-yaml",
  yml: "application/x-yaml",
  toml: "application/toml",
  ini: "text/plain",
  cfg: "text/plain",
  conf: "text/plain",
  config: "text/plain",

  // Documentation
  rst: "text/x-rst",
  tex: "text/x-tex",

  // Database
  sql: "application/sql",

  // Vector graphics
  svg: "image/svg+xml",

  // Other common text formats
  gitignore: "text/plain",
  dockerfile: "text/plain",
  makefile: "text/plain",
  env: "text/plain",
  log: "text/plain",
  license: "text/plain",
  README: "text/plain",
} as const;

/**
 * Get MIME type from filename using custom mapping
 * Returns "text/plain" as fallback for unknown types
 */
export function getMimeTypeFromFilename(filename: string): string {
  const extension = getFileExtension(filename);
  return EXTENSION_TO_MIME_TYPE[extension] || "text/plain";
}

/**
 * Extract file extension from filename (without dot, lowercase)
 */
function getFileExtension(filename: string): string {
  if (!filename) {
    return "";
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    // No extension or ends with dot - check for special filenames
    const lowerFilename = filename.toLowerCase();
    if (
      lowerFilename === "makefile" ||
      lowerFilename === "dockerfile" ||
      lowerFilename === "readme" ||
      lowerFilename === "license"
    ) {
      return lowerFilename;
    }
    return "";
  }

  return filename.slice(lastDotIndex + 1).toLowerCase();
}

/**
 * Check if a MIME type represents text content
 * Uses mime-db data to determine if a MIME type should be treated as text
 */
export function isTextMimeType(mimeType: string): boolean {
  if (!mimeType) {
    return false;
  }

  // Check if it starts with "text/"
  if (mimeType.startsWith("text/")) {
    return true;
  }

  return textBasedTypes.includes(mimeType);
}

// Check for other common text-based MIME types
const textBasedTypes = [
  "application/json",
  "application/javascript",
  "text/javascript",
  "application/typescript",
  "application/xml",
  "application/xhtml+xml",
  "application/x-yaml",
  "application/toml",
  "application/sql",
  "application/x-sh",
  "application/x-bash",
  "application/x-csh",
  "application/x-fish",
  "application/x-zsh",
  "application/x-httpd-php",
  "application/x-perl",
  "application/x-python-code",
  "application/x-ruby",
  "application/x-tcl",
  "application/rdf+xml",
  "application/atom+xml",
  "application/rss+xml",
  "image/svg+xml",
];

/**
 * Get all supported file extensions based on text-based MIME types
 * Returns an array of file extensions (with dots) that are considered text files
 */
export const getSupportedFileExtensions = (): string[] => {
  const extensions = new Set<string>();

  // Get all extensions from our custom mapping that correspond to text-based MIME types
  for (const [extension, mimeType] of Object.entries(EXTENSION_TO_MIME_TYPE)) {
    if (isTextMimeType(mimeType)) {
      // Only add extensions with dots, skip special filenames
      if (
        extension !== "makefile" &&
        extension !== "dockerfile" &&
        extension !== "readme" &&
        extension !== "license"
      ) {
        extensions.add(`.${extension}`);
      }
    }
  }

  return Array.from(extensions).sort();
};

/**
 * Cached result of supported file extensions for performance
 */
export const SUPPORTED_FILE_EXTENSIONS = getSupportedFileExtensions();
